import React, { useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import apiClient from '../services/api';
import {
  Download,
  Upload,
  AlertTriangle,
  XCircle,
  Shield,
  CheckCircle,
} from 'lucide-react';

type ImportMode = 'create' | 'update' | 'delete';
type FileFormat = 'csv' | 'json';

type ImportResponse = {
  created: number;
  updated: number;
  deleted: number;
  errors: { line: number; message: string }[];
};

function downloadText(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const DataImportExport: React.FC = () => {
  const permissions = usePermissions();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<ImportMode>('create');
  const [exportFormat, setExportFormat] = useState<FileFormat>('csv');
  const [busy, setBusy] = useState<'export' | 'import' | 'model' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<ImportResponse['errors']>([]);

  const canImport =
    permissions.canImportCreate || permissions.canImportUpdate || permissions.canImportDelete;

  const apiError = (err: unknown) => {
    const response = err as { response?: { data?: { message?: string | string[] } } };
    const raw = response.response?.data?.message;
    if (Array.isArray(raw)) return raw.join(' ');
    return raw || 'L’échange avec le serveur a échoué';
  };

  const handleExport = async () => {
    if (!permissions.canExport || busy) return;
    setBusy('export');
    setError(null);
    setMessage(null);
    try {
      const { data } = await apiClient.get('/objets/export', { params: { format: exportFormat } });
      downloadText(data.filename, data.content, data.mime);
      setMessage(`${data.count} objet(s) exporté(s). L’export est inscrit au journal.`);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(null);
    }
  };

  const handleModel = async () => {
    if (busy) return;
    setBusy('model');
    setError(null);
    try {
      const { data } = await apiClient.get('/objets/export/modele');
      downloadText(data.filename, data.content, data.mime);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !canImport || busy) return;
    if (importMode === 'create' && !permissions.canImportCreate) return;
    if (importMode === 'update' && !permissions.canImportUpdate) return;
    if (importMode === 'delete' && !permissions.canImportDelete) return;

    setBusy('import');
    setError(null);
    setMessage(null);
    setRowErrors([]);
    try {
      const content = await selectedFile.text();
      const format: FileFormat = selectedFile.name.toLowerCase().endsWith('.json') ? 'json' : 'csv';
      const { data } = await apiClient.post<ImportResponse>('/objets/import', {
        mode: importMode,
        format,
        content,
      });
      const applied = data.created + data.updated + data.deleted;
      setMessage(
        `${applied} ligne(s) appliquée(s)` +
          (data.created ? ` · ${data.created} créée(s)` : '') +
          (data.updated ? ` · ${data.updated} mise(s) à jour` : '') +
          (data.deleted ? ` · ${data.deleted} supprimée(s)` : '') +
          (data.errors.length ? ` · ${data.errors.length} erreur(s)` : ''),
      );
      setRowErrors(data.errors);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Shield className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Import / export des objets</h3>
      </div>

      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 space-y-2">
        <p>
          Le fichier reprend les objets déjà en inventaire : nom, SKU, catégorie, sous-catégorie,
          quantité, état, besoin d’achat, pièce, zone, box, couleurs et remarques. Les photos ne
          sont pas dans le fichier.
        </p>
        <p>
          Séparateur CSV : point-virgule, UTF-8. La box, la zone et la pièce doivent déjà exister
          (code ou nom). La catégorie aussi. Une seule localisation est retenue, dans l’ordre box,
          zone, pièce.
        </p>
        <p>
          Création : laissez <strong>id</strong> vide. Mise à jour et suppression : gardez l’
          <strong>id</strong> de l’export. La suppression par fichier est réservée à l’administrateur.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Download className="h-5 w-5 text-green-600" />
            <h4 className="text-md font-semibold text-gray-900">Export</h4>
          </div>

          {permissions.canExport ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="export-format">
                  Format
                </label>
                <select
                  id="export-format"
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value as FileFormat)}
                  className="w-full min-h-11 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleExport}
                disabled={busy !== null}
                className="w-full min-h-11 flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                <span>{busy === 'export' ? 'Export…' : 'Exporter les objets'}</span>
              </button>
              <button
                type="button"
                onClick={handleModel}
                disabled={busy !== null}
                className="text-sm text-blue-700 hover:text-blue-900 underline disabled:opacity-50"
              >
                {busy === 'model' ? 'Téléchargement…' : 'Télécharger le modèle CSV'}
              </button>
            </div>
          ) : (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center space-x-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">
                  L’export est réservé aux inventoristes, gestionnaires et administrateurs.
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Upload className="h-5 w-5 text-blue-600" />
            <h4 className="text-md font-semibold text-gray-900">Import</h4>
          </div>

          {canImport ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="import-mode">
                  Mode
                </label>
                <select
                  id="import-mode"
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value as ImportMode)}
                  className="w-full min-h-11 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {permissions.canImportCreate && <option value="create">Création</option>}
                  {permissions.canImportUpdate && <option value="update">Mise à jour</option>}
                  {permissions.canImportDelete && <option value="delete">Suppression</option>}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="import-file">
                  Fichier CSV ou JSON
                </label>
                <input
                  id="import-file"
                  type="file"
                  accept=".csv,.json,text/csv,application/json"
                  onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                  className="w-full min-h-11 px-3 py-2 border border-gray-300 rounded-md"
                />
                {selectedFile && (
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} Ko)
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleImport}
                disabled={!selectedFile || busy !== null}
                className="w-full min-h-11 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Upload className="h-4 w-4" />
                <span>{busy === 'import' ? 'Import…' : 'Importer'}</span>
              </button>
            </div>
          ) : (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center space-x-2">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-800">
                  L’import est réservé aux inventoristes, gestionnaires et administrateurs.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800 flex items-start gap-2">
          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{message}</span>
        </div>
      )}
      {error && (
        <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
          {error}
        </div>
      )}
      {rowErrors.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-red-800">
          {rowErrors.map((row) => (
            <li key={`${row.line}-${row.message}`}>
              Ligne {row.line} : {row.message}
            </li>
          ))}
        </ul>
      )}

      {permissions.canImportDelete && (
        <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-800">
              Le mode suppression retire définitivement les objets dont l’id est dans le fichier.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataImportExport;
